import { inject, Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, Router, RouterStateSnapshot } from '@angular/router';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { AuthService } from './services/auth.service';
import { MemoryService } from './services/memory.service';
import { ToastrService } from 'ngx-toastr';
import { ExternalResponse } from './models/settings/external-response';

@Injectable({
    providedIn: 'root',
})
export class UserConnectedGuard {
    private toastrService = inject(ToastrService);

    private CESAR_ACCESS_TOKEN = '__access_token__';
    private RESPONSE = 'response';

    constructor(
        private authService: AuthService,
        private memoryService: MemoryService,
        private router: Router
    ) {}

    canActivate(next: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> {
        const connectedUser = this.memoryService.getConnectedUser();
        const accessToken = state.root.queryParams[this.CESAR_ACCESS_TOKEN];
        if (!!accessToken) {
            console.log(`Check if token is valide`);
            return this.authService.getCurrentUser(accessToken).pipe(
                map((x) => {
                    // Redirige vers la page demandée en enlevant le paramètre du token
                    const params = { ...next.queryParams };
                    delete params[this.CESAR_ACCESS_TOKEN];
                    this.router.navigate([state.url.substring(0, state.url.indexOf('?'))], { queryParams: params });
                    return true;
                }),
                catchError((error) => {
                    // Redirige vers la page de login en cas d'erreur.
                    console.log(`User in not connected : Token is not valide`);
                    this.memoryService.setConnectedUser(null);
                    this.memoryService.selectOrganization(null);
                    this.router.navigate(['/login']);
                    throw error;
                })
            );
        } else if (!!connectedUser) {
            if (next.queryParams[this.RESPONSE]) {
                const response = JSON.parse(next.queryParams[this.RESPONSE]) as ExternalResponse;

                if (response) {
                    if (response.code === 200) {
                        this.toastrService.success(response.message, '', { enableHtml: true });
                    } else {
                        this.toastrService.warning(response.message, '', { enableHtml: true });
                    }
                }

                this.router.navigate([]);
            }

            console.log(`Check if user is connected`);
            return this.authService.checkToken().pipe(
                map((x) => {
                    console.log(`User is connected`);
                    return true;
                }),
                catchError((error) => {
                    console.log(`User in not connected : Token is expired`, error);
                    this.memoryService.setConnectedUser(null);
                    this.memoryService.selectOrganization(null);
                    this.router.navigate(['/login']);
                    throw error;
                })
            );
        } else {
            if (next.queryParams[this.RESPONSE]) {
                const response = JSON.parse(next.queryParams[this.RESPONSE]) as ExternalResponse;

                if (response) {
                    if (response.code === 200) {
                        this.toastrService.success(response.message, '', { enableHtml: true });
                    } else {
                        this.toastrService.warning(response.message, '', { enableHtml: true });
                    }
                }

                this.router.navigate([]);
            }

            console.log(`User in not connected`);
            this.memoryService.setConnectedUser(null);
            this.memoryService.selectOrganization(null);
            this.router.navigate(['/login']);
            return of(false);
        }
    }
}
